
import { defineComponent, ref, computed, PropType } from "vue";
import { useI18n } from "vue-i18n";

import enUK from "@/locales/theme/en-UK.json";
import BasicButton from "@/components/BasicButton.vue";
import { Theme } from "@/helpers/project/typesDefinition";
import Modal from "@/components/Modal.vue";
import ConfirmModal from "@/components/ConfirmModal.vue";
import { useMutation, UseMutationOptions } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import { MERGE_THEMES } from "@/graphql/project/mutations";

type MessageSchema = typeof enUK;

export default defineComponent({
  props: {
    themes: {
      type: Array as PropType<Theme[]>,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
    refetchQueries: {
      type: Object as PropType<UseMutationOptions["refetchQueries"]>,
      required: false,
    },
  },
  components: {
    Modal,
    ConfirmModal,
    BasicButton,
  },
  emits: ["close"],

  setup(props, { emit }) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });
    const toast = useToast();

    const newThemeName = ref("");
    const selectedThemeIds = ref<string[]>([]);

    const selectedThemes = computed(() =>
      props.themes.filter((theme) => selectedThemeIds.value.includes(theme.id)),
    );

    function closeModal(): void {
      emit("close");
    }

    const isConfirmModalVisible = ref(false);
    const showConfirmModal = () => {
      isConfirmModalVisible.value = true;
    };
    const closeConfirmModal = () => {
      isConfirmModalVisible.value = false;
    };

    const {
      mutate: mergeThemes,
      onError: onMergeError,
      onDone: onMergeDone,
    } = useMutation(MERGE_THEMES, {
      refetchQueries: props.refetchQueries,
    });

    const runMergeThemes = () => {
      mergeThemes({
        themeIds: selectedThemeIds.value,
        projectId: props.projectId,
        newName: newThemeName.value,
      });
    };

    onMergeError((error) => {
      logErrorMessages(error);
      toast.error(t("errors.mergeThemes", { message: error.message }));
    });

    onMergeDone(async (result) => {
      if (result.data?.mergeThemes?.status !== "ok") {
        const errorResponse = result.data?.mergeThemes?.error;
        console.log(errorResponse);
        toast.error(t("errors.mergeThemes", { message: errorResponse }));
      } else {
        toast.success(t("success.mergeThemes"));
        closeModal();
        closeConfirmModal();
      }
    });

    return {
      t,
      newThemeName,
      selectedThemeIds,
      closeModal,
      isConfirmModalVisible,
      showConfirmModal,
      closeConfirmModal,
      mergeThemes,
      selectedThemes,
      runMergeThemes,
    };
  },
});
